import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, ChevronLeft, ChevronRight, Eye, ArrowLeft,
  ArrowUpDown, ArrowUp, ArrowDown, X, FolderKanban, Layers, Lock,
} from 'lucide-react';
import { projectsApi } from '../../services/api';
import type { Project, Group, Pagination } from '../../types';
import GroupDetailModal from './GroupDetailModal';

/* ───────── Constants ───────── */

type ProjectSortCol = 'name' | 'owner' | 'team_name' | 'modified_date';
type GroupSortCol = 'name' | 'owner' | 'type' | 'modified_date';
type SortDir = 'asc' | 'desc';

const TYPE_LABELS: Record<string, string> = {
  V: 'Validation',
  R: 'Reconciliation',
};

const TYPE_COLORS: Record<string, string> = {
  V: 'bg-green-500/20 text-green-400 border-green-500/30',
  R: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

function typeBadge(type: string) {
  const cls = TYPE_COLORS[type] || 'bg-cargo-panel text-cargo-muted border-cargo-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/* ───────── Component ───────── */

const ProjectsPage: React.FC = () => {
  /* ── State: which view ── */
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  /* ── Projects state ── */
  const [projects, setProjects] = useState<Project[]>([]);
  const [projLoading, setProjLoading] = useState(false);
  const [projCount, setProjCount] = useState(0);
  const [projPages, setProjPages] = useState(1);
  const [projPage, setProjPage] = useState(1);
  const [projPerPage, setProjPerPage] = useState(10);
  const [projSearch, setProjSearch] = useState('');
  const [projSortCol, setProjSortCol] = useState<ProjectSortCol>('modified_date');
  const [projSortDir, setProjSortDir] = useState<SortDir>('desc');

  /* ── Groups state ── */
  const [groups, setGroups] = useState<Group[]>([]);
  const [grpLoading, setGrpLoading] = useState(false);
  const [grpCount, setGrpCount] = useState(0);
  const [grpPages, setGrpPages] = useState(1);
  const [grpPage, setGrpPage] = useState(1);
  const [grpPerPage, setGrpPerPage] = useState(10);
  const [grpSearch, setGrpSearch] = useState('');
  const [grpSortCol, setGrpSortCol] = useState<GroupSortCol>('modified_date');
  const [grpSortDir, setGrpSortDir] = useState<SortDir>('desc');

  /* ── Expanded rows ── */
  const [expandedRules, setExpandedRules] = useState<number | null>(null);
  const [expandedTags, setExpandedTags] = useState<number | null>(null);

  /* ── Modal ── */
  const [detailGroup, setDetailGroup] = useState<Group | null>(null);

  /* ────────────────── Fetch Projects ────────────────── */

  const fetchProjects = useCallback(async () => {
    setProjLoading(true);
    try {
      const params: Record<string, any> = {
        page: projPage,
        items_per_page: projPerPage,
        sort_col: projSortCol,
        sort_dir: projSortDir,
      };
      if (projSearch.trim()) params.search = projSearch.trim();

      const resp = await projectsApi.list(params);
      const payload = resp.data.payload;
      const list = payload?.projects || payload || [];
      setProjects(Array.isArray(list) ? list : []);
      const pagination: Pagination | undefined = payload?.pagination;
      if (pagination) {
        setProjCount(pagination.count || 0);
        setProjPages(pagination.pages || Math.ceil((pagination.count || 0) / projPerPage) || 1);
      } else {
        setProjCount(Array.isArray(list) ? list.length : 0);
        setProjPages(1);
      }
    } catch {
      setProjects([]);
      setProjCount(0);
      setProjPages(1);
    } finally {
      setProjLoading(false);
    }
  }, [projPage, projPerPage, projSearch, projSortCol, projSortDir]);

  useEffect(() => {
    if (!selectedProject) fetchProjects();
  }, [fetchProjects, selectedProject]);

  /* ────────────────── Fetch Groups ────────────────── */

  const fetchGroups = useCallback(async () => {
    if (!selectedProject) return;
    setGrpLoading(true);
    try {
      const params: Record<string, any> = {
        page: grpPage,
        items_per_page: grpPerPage,
        sort_col: grpSortCol,
        sort_dir: grpSortDir,
      };
      if (grpSearch.trim()) params.search = grpSearch.trim();

      const resp = await projectsApi.groups(selectedProject.id, params);
      const payload = resp.data.payload;
      const list = payload?.groups || payload || [];
      setGroups(Array.isArray(list) ? list : []);
      const pagination: Pagination | undefined = payload?.pagination;
      if (pagination) {
        setGrpCount(pagination.count || 0);
        setGrpPages(pagination.pages || Math.ceil((pagination.count || 0) / grpPerPage) || 1);
      } else {
        setGrpCount(Array.isArray(list) ? list.length : 0);
        setGrpPages(1);
      }
    } catch {
      setGroups([]);
      setGrpCount(0);
      setGrpPages(1);
    } finally {
      setGrpLoading(false);
    }
  }, [selectedProject, grpPage, grpPerPage, grpSearch, grpSortCol, grpSortDir]);

  useEffect(() => {
    if (selectedProject) fetchGroups();
  }, [fetchGroups, selectedProject]);

  /* ────────────────── Sort helpers ────────────────── */

  const handleProjSort = (col: ProjectSortCol) => {
    if (projSortCol === col) {
      setProjSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setProjSortCol(col);
      setProjSortDir('asc');
    }
    setProjPage(1);
  };

  const handleGrpSort = (col: GroupSortCol) => {
    if (grpSortCol === col) {
      setGrpSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setGrpSortCol(col);
      setGrpSortDir('asc');
    }
    setGrpPage(1);
  };

  const handleSelectProject = (proj: Project) => {
    setSelectedProject(proj);
    setGrpPage(1);
    setGrpSearch('');
    setGrpSortCol('modified_date');
    setGrpSortDir('desc');
    setExpandedRules(null);
    setExpandedTags(null);
  };

  const handleBack = () => {
    setSelectedProject(null);
    setGroups([]);
    setGrpCount(0);
    setExpandedRules(null);
    setExpandedTags(null);
  };

  /* ────────────────── Sort Icon ────────────────── */

  function SortIcon<T extends string>({ col, activeCol, activeDir }: { col: T; activeCol: T; activeDir: SortDir }) {
    if (activeCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return activeDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  /* ────────────────── Pagination ────────────────── */

  function PaginationBar({
    page, totalPages, perPage, onPageChange, onPerPageChange,
  }: {
    page: number; totalPages: number; perPage: number;
    onPageChange: (p: number) => void; onPerPageChange: (n: number) => void;
  }) {
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-cargo-border">
        <div className="flex items-center gap-2 text-sm text-cargo-muted">
          <span>Rows per page:</span>
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="pl-2 pr-7 py-1 rounded bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                       outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-cargo-muted">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────────── Search bar ────────────────── */

  function SearchBar({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
    return (
      <div className="relative flex-1 min-w-[280px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-8 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                     text-cargo-text placeholder-cargo-muted/50 text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-cargo-muted hover:text-cargo-text"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     PROJECTS LIST VIEW
     ══════════════════════════════════════════════════ */

  if (!selectedProject) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2">
            <FolderKanban className="w-6 h-6 text-cargo-accent" />
            <h1 className="text-2xl font-bold text-cargo-text">Projects</h1>
          </div>
          <p className="text-sm text-cargo-muted mt-1">
            {projCount} project{projCount !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Filter bar */}
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
          <SearchBar
            value={projSearch}
            onChange={(v) => { setProjSearch(v); setProjPage(1); }}
            placeholder="Search projects by name..."
          />
        </div>

        {/* Table */}
        <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cargo-border">
                  {([
                    ['name', 'Project Name', true],
                    ['', 'Groups', false],
                    ['owner', 'Owner', true],
                    ['team_name', 'Team', true],
                    ['modified_date', 'Modified', true],
                  ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                    <th
                      key={i}
                      className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                  ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                      onClick={sortable ? () => handleProjSort(col as ProjectSortCol) : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortable && col && <SortIcon col={col as ProjectSortCol} activeCol={projSortCol} activeDir={projSortDir} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projLoading ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center">
                      <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                      <p className="text-cargo-muted text-sm mt-2">Loading projects...</p>
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-cargo-muted text-sm">
                      No projects found.
                    </td>
                  </tr>
                ) : (
                  projects.map((proj) => (
                    <tr
                      key={proj.id}
                      className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors cursor-pointer"
                      onClick={() => handleSelectProject(proj)}
                    >
                      {/* Project Name + description */}
                      <td className="px-4 py-3 max-w-[320px]">
                        <p className="text-cargo-text font-medium text-sm truncate">{proj.name}</p>
                        {proj.description && (
                          <p className="text-xs text-cargo-muted mt-0.5 truncate" title={proj.description}>
                            {proj.description}
                          </p>
                        )}
                      </td>

                      {/* Groups summary */}
                      <td className="px-4 py-3">
                        {proj.group_details?.groups?.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent">
                              {proj.group_details.groups[0].name}
                            </span>
                            {(proj.group_details.groups.length - 1 + (proj.group_details.extra_count || 0)) > 0 && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted">
                                +{proj.group_details.groups.length - 1 + (proj.group_details.extra_count || 0)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-cargo-muted/40 text-xs">—</span>
                        )}
                      </td>

                      {/* Owner */}
                      <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{proj.owner}</td>

                      {/* Team */}
                      <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{proj.team_name}</td>

                      {/* Modified Date */}
                      <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDate(proj.modified_date)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!projLoading && projects.length > 0 && (
            <PaginationBar
              page={projPage}
              totalPages={projPages}
              perPage={projPerPage}
              onPageChange={setProjPage}
              onPerPageChange={(n) => { setProjPerPage(n); setProjPage(1); }}
            />
          )}
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     GROUPS VIEW (project selected)
     ══════════════════════════════════════════════════ */

  return (
    <div className="space-y-6">
      {/* Breadcrumb + back */}
      <div>
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-cargo-muted hover:text-cargo-accent transition-colors mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>
        <div className="flex items-center gap-2">
          <Layers className="w-6 h-6 text-cargo-accent" />
          <div>
            <h1 className="text-2xl font-bold text-cargo-text">{selectedProject.name}</h1>
            <p className="text-sm text-cargo-muted mt-0.5">
              {grpCount} group{grpCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
        <SearchBar
          value={grpSearch}
          onChange={(v) => { setGrpSearch(v); setGrpPage(1); }}
          placeholder="Search groups by name..."
        />
      </div>

      {/* Groups table */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cargo-border">
                {([
                  ['name', 'Group Name', true],
                  ['type', 'Type', true],
                  ['', 'Applied Rules', false],
                  ['owner', 'Owner', true],
                  ['', 'Tags', false],
                  ['modified_date', 'Modified', true],
                  ['', '', false],
                ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                    onClick={sortable ? () => handleGrpSort(col as GroupSortCol) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortable && col && <SortIcon col={col as GroupSortCol} activeCol={grpSortCol} activeDir={grpSortDir} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grpLoading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                    <p className="text-cargo-muted text-sm mt-2">Loading groups...</p>
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-cargo-muted text-sm">
                    No groups found.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr
                    key={group.id}
                    className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors"
                  >
                    {/* Group Name + description + lock */}
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="flex items-center gap-1.5">
                        <p className="text-cargo-text font-medium text-sm truncate">{group.name}</p>
                        {group.meta?.locked && (
                          <span title="Locked"><Lock className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" /></span>
                        )}
                      </div>
                      {group.description && (
                        <p className="text-xs text-cargo-muted mt-0.5 truncate" title={group.description}>
                          {group.description}
                        </p>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">{typeBadge(group.type)}</td>

                    {/* Applied Rules */}
                    <td className="px-4 py-3">
                      {group.applied_rules?.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent font-mono truncate max-w-[180px]">
                            {group.applied_rules[0].rule}
                          </span>
                          {group.applied_rules.length > 1 && (
                            expandedRules === group.id ? (
                              <>
                                {group.applied_rules.slice(1).map((r) => (
                                  <span key={r.id} className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent font-mono truncate max-w-[180px]">
                                    {r.rule}
                                  </span>
                                ))}
                                <button
                                  onClick={() => setExpandedRules(null)}
                                  className="text-xs text-cargo-muted hover:text-cargo-text"
                                >
                                  less
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setExpandedRules(group.id)}
                                className="px-1.5 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted hover:text-cargo-text"
                              >
                                +{group.applied_rules.length - 1}
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-cargo-muted/40 text-xs">—</span>
                      )}
                    </td>

                    {/* Owner */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{group.owner}</td>

                    {/* Tags */}
                    <td className="px-4 py-3">
                      {group.tags?.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent">
                            {group.tags[0]}
                          </span>
                          {group.tags.length > 1 && (
                            expandedTags === group.id ? (
                              <>
                                {group.tags.slice(1).map((t) => (
                                  <span key={t} className="px-2 py-0.5 text-xs rounded bg-cargo-accent/10 text-cargo-accent">
                                    {t}
                                  </span>
                                ))}
                                <button
                                  onClick={() => setExpandedTags(null)}
                                  className="text-xs text-cargo-muted hover:text-cargo-text"
                                >
                                  less
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setExpandedTags(group.id)}
                                className="px-1.5 py-0.5 text-xs rounded bg-cargo-panel text-cargo-muted hover:text-cargo-text"
                              >
                                +{group.tags.length - 1}
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-cargo-muted/40 text-xs">—</span>
                      )}
                    </td>

                    {/* Modified */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDate(group.modified_date)}</td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setDetailGroup(group)}
                        className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!grpLoading && groups.length > 0 && (
          <PaginationBar
            page={grpPage}
            totalPages={grpPages}
            perPage={grpPerPage}
            onPageChange={setGrpPage}
            onPerPageChange={(n) => { setGrpPerPage(n); setGrpPage(1); }}
          />
        )}
      </div>

      {/* Group detail modal */}
      {detailGroup && (
        <GroupDetailModal group={detailGroup} onClose={() => setDetailGroup(null)} />
      )}
    </div>
  );
};

export default ProjectsPage;
